export const DEFAULT_MINIMUM_CLIENT_AGE = 18;
export const ALLOWED_MINIMUM_CLIENT_AGES = [
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  DEFAULT_MINIMUM_CLIENT_AGE,
] as const;

export type AllowedMinimumClientAge =
  (typeof ALLOWED_MINIMUM_CLIENT_AGES)[number];
type MinimumClientAgeString = `${AllowedMinimumClientAge}`;
export type MinorAgeClassification = "UNKNOWN" | "ADULT" | "MINOR";
export type MinorClientAgeRangeMinimum = Exclude<
  AllowedMinimumClientAge,
  typeof DEFAULT_MINIMUM_CLIENT_AGE
>;

export const MINIMUM_CLIENT_AGE_STRING_VALUES = ALLOWED_MINIMUM_CLIENT_AGES.map(
  (age) => String(age),
) as [MinimumClientAgeString, ...MinimumClientAgeString[]];
export const MINOR_CLIENT_AGE_RANGE_MINIMUM_AGES =
  ALLOWED_MINIMUM_CLIENT_AGES.filter(
    (age): age is MinorClientAgeRangeMinimum =>
      age !== DEFAULT_MINIMUM_CLIENT_AGE,
  )
    .slice()
    .reverse();

export const minimumClientAgeOptions = MINIMUM_CLIENT_AGE_STRING_VALUES.slice()
  .reverse()
  .map((value) => ({
    value,
    label:
      value === "18"
        ? "Radim samo sa punoletnima"
        : `Radim sa klijentima od ${value} godina`,
  }));

export const minimumClientAgeLabel = "Najmlađi uzrast klijenata";

export const minimumClientAgeDescription =
  "Klijenti mlađi od ovog uzrasta ne mogu da zakažu seansu. Oblasti rada ne menjaju ovu granicu.";

export function getMinimumClientAgePublicLabel(minimumClientAge: number) {
  return minimumClientAge === DEFAULT_MINIMUM_CLIENT_AGE
    ? "Rad samo sa punoletnim klijentima"
    : `Rad sa klijentima od ${minimumClientAge} godina`;
}

export function normalizeDateOnly(dateInput: Date) {
  return new Date(
    Date.UTC(
      dateInput.getUTCFullYear(),
      dateInput.getUTCMonth(),
      dateInput.getUTCDate(),
    ),
  );
}

export function isValidDateOnlyString(dateInput: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput);

  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCFullYear(year);

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function parseDateOnly(dateInput: string) {
  if (!isValidDateOnlyString(dateInput)) {
    throw new Error("Invalid date-only value.");
  }

  const [year, month, day] = dateInput.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day!));
  date.setUTCFullYear(year!);
  return date;
}

export function formatDateOnly(date: Date) {
  return normalizeDateOnly(date).toISOString().slice(0, 10);
}

export function calculateAgeFromDateOfBirth(
  dateOfBirth: Date,
  now = new Date(),
) {
  const birth = normalizeDateOnly(dateOfBirth);
  const current = normalizeDateOnly(now);

  let age = current.getUTCFullYear() - birth.getUTCFullYear();
  const monthDiff = current.getUTCMonth() - birth.getUTCMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && current.getUTCDate() < birth.getUTCDate())
  ) {
    age -= 1;
  }

  return age;
}

export function calculateAgeFromDateOnlyString(
  dateInput: string,
  now = new Date(),
) {
  if (!isValidDateOnlyString(dateInput)) {
    return null;
  }

  return calculateAgeFromDateOfBirth(parseDateOnly(dateInput), now);
}

export function classifyAge(
  dateOfBirth: Date | null | undefined,
): MinorAgeClassification {
  if (!dateOfBirth) {
    return "UNKNOWN";
  }

  return calculateAgeFromDateOfBirth(dateOfBirth) >= 18 ? "ADULT" : "MINOR";
}

export function isMinorDateOfBirth(dateInput: string, now = new Date()) {
  const age = calculateAgeFromDateOnlyString(dateInput, now);
  return age !== null && age < 18;
}

export function isAllowedMinimumClientAge(value: number) {
  return ALLOWED_MINIMUM_CLIENT_AGES.includes(value as AllowedMinimumClientAge);
}
